import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, UtensilsCrossed, GripVertical, Eye, EyeOff } from 'lucide-react';
import { deleteTable, insertTable, updateTable, uploadImage } from '@/lib/api';
import type { Category, Product } from '@/types';
import { formatPrice, normalizePublicImageUrl } from '@/lib/format';
import { Modal } from '@/components/Modal';
import { ImageUpload } from '@/components/ImageUpload';

interface ProductManagerProps {
  categories: Category[];
  products: Product[];
  onRefresh: () => void;
}

export function ProductManager({ categories, products, onRefresh }: ProductManagerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    is_active: true,
  sort_order: 0,
  });
  const [saving, setSaving] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

  const openNew = () => {
    setEditing(null);
    setPendingImageFile(null);
    setForm({
      name: '',
      description: '',
      price: '',
      category_id: categories[0]?.id || '',
      image_url: '',
      is_active: true,
      sort_order: 0,
    });
    setModalOpen(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setPendingImageFile(null);
    setForm({
      name: p.name,
      description: p.description,
      price: String(p.price),
      category_id: p.category_id,
      image_url: p.image_url,
      is_active: p.is_active,
      sort_order: p.sort_order,
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.category_id) return;
    setSaving(true);
    try {
      let imageUrl = form.image_url;
      if (pendingImageFile) {
        const upload = await uploadImage(pendingImageFile, form.name);
        imageUrl = upload.url;
      }

      const payload = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price) || 0,
        category_id: Number(form.category_id),
        image_url: imageUrl,
        is_active: form.is_active,
        sort_order: form.sort_order,
      };
      if (editing) {
        await updateTable('products', { ...payload, id: Number(editing.id) });
      } else {
        await insertTable('products', payload);
      }
      setModalOpen(false);
      setPendingImageFile(null);
      onRefresh();
    } catch {
      alert('Erro ao salvar produto.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p: Product) => {
    if (!confirm(`Excluir "${p.name}"?`)) return;
    await deleteTable('products', Number(p.id));
    onRefresh();
  };

  const toggleActive = async (p: Product) => {
    await updateTable('products', { id: Number(p.id), is_active: !p.is_active });
    onRefresh();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-warm-900">Produtos</h2>
        <button onClick={openNew} className="btn-primary">
          <Plus size={18} /> Novo Produto
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-12 text-center">
          <p className="text-warm-500">Crie uma categoria primeiro para adicionar produtos.</p>
        </div>
      ) : products.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-12 text-center">
          <UtensilsCrossed size={40} className="text-warm-300" />
          <p className="mt-3 text-warm-500">Nenhum produto cadastrado.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {products.map((p) => {
            const cat = categories.find((c) => c.id === p.category_id);
            return (
              <div key={p.id} className="card flex items-center gap-3 p-3">
                {p.image_url ? (
                  <img src={normalizePublicImageUrl(p.image_url)} alt={p.name} className="h-14 w-14 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-warm-100 text-warm-300">
                    <UtensilsCrossed size={20} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="truncate font-medium text-warm-900">{p.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-warm-500">
                    <span>{cat?.name || 'Sem categoria'}</span>
                    <span>·</span>
                    <span className="font-semibold text-brand-600">{formatPrice(p.price)}</span>
                  </div>
                </div>
                <button
                  onClick={() => toggleActive(p)}
                  className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                    p.is_active
                      ? 'bg-green-50 text-green-600 hover:bg-green-100'
                      : 'bg-warm-100 text-warm-400 hover:bg-warm-200'
                  }`}
                  title={p.is_active ? 'Ativo (clique para desativar)' : 'Inativo (clique para ativar)'}
                >
                  {p.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-warm-100 text-warm-600 transition-colors hover:bg-warm-200"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => remove(p)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors hover:bg-red-100"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar Produto' : 'Novo Produto'}
      >
        <div className="space-y-4">
          <ImageUpload
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Foto do produto"
            fileNameHint={form.name}
            uploadOnSelect={false}
            onFileSelect={setPendingImageFile}
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Nome</label>
            <input
              className="input-field"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Pizza Margherita"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Descrição</label>
            <textarea
              className="input-field min-h-20 resize-none"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Ingredientes, detalhes..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-warm-700">Preço (R$)</label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="0,00"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-warm-700">Ordem</label>
              <input
                type="number"
                className="input-field"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Categoria</label>
            <select
              className="input-field"
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="h-5 w-5 rounded accent-brand-500"
            />
            <span className="text-sm font-medium text-warm-700">Produto ativo (visível para clientes)</span>
          </label>
          <button onClick={save} disabled={saving} className="btn-primary w-full">
            {saving ? 'Salvando...' : 'Salvar Produto'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
